import React from 'react'
import {
  Shield,
  FileText,
  Mail,
  Bell,
  Cpu,
  BarChart3,
  Webhook,
  CreditCard,
  Trash2,
  Eye,
  ToggleRight,
  AlertTriangle,
  Save,
  History,
  Database,
  CheckCircle2,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─── Info Box ─────────────────────────────────────────────────────────────────
function InfoBox({ type = 'info', children }) {
  const styles = {
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300',
    success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300',
  }
  return (
    <div className={`p-3 rounded-lg border text-sm ${styles[type]}`}>
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Page Header Mockup
// ─────────────────────────────────────────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
          <Shield className="h-4 w-4 text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Data Retention</div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">Configure automatic cleanup of old data to manage storage costs and compliance.</div>
        </div>
      </div>
      <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-gray-800 rounded-md w-fit">
        <div className="px-3 py-1.5 text-[10px] font-medium rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm flex items-center gap-1">
          <Database className="h-3 w-3" /> Policies
        </div>
        <div className="px-3 py-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <History className="h-3 w-3" /> Deletion Log
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Policy Card Mockup
// ─────────────────────────────────────────────────────────────────────────────
function PolicyCardMockup() {
  return (
    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/40">
            <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">Documents / PDFs</div>
            <div className="text-[9px] text-gray-500">Uploaded PDF files and their metadata.</div>
          </div>
        </div>
        <ToggleRight className="h-5 w-5 text-blue-600" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[9px] text-gray-500 mb-0.5">Retention Period</div>
          <div className="h-7 px-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center text-[10px] text-gray-700 dark:text-gray-300">180 days</div>
        </div>
        <div>
          <div className="text-[9px] text-gray-500 mb-0.5">Delete blob files?</div>
          <div className="h-7 px-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-center gap-1 text-[10px] text-red-700 dark:text-red-400">
            <Trash2 className="h-3 w-3" /> Yes - delete PDFs
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-2">
          <div className="px-2 py-1 text-[9px] rounded border border-gray-200 dark:border-gray-700 text-gray-500 flex items-center gap-1">
            <Eye className="h-3 w-3" /> Preview
          </div>
          <span className="text-[9px] text-amber-600 flex items-center gap-0.5">
            <AlertTriangle className="h-3 w-3" /> 247 items would be deleted
          </span>
        </div>
        <div className="px-2 py-1 text-[9px] rounded bg-blue-600 text-white flex items-center gap-1">
          <Save className="h-3 w-3" /> Save
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Entity Types Table
// ─────────────────────────────────────────────────────────────────────────────
function EntityTypesTable() {
  const entities = [
    { icon: FileText, name: 'Documents / PDFs', desc: 'Uploaded files + blob storage', default: '90–180 days', priority: 'HIGH' },
    { icon: FileText, name: 'Requests', desc: 'Work items + extracted data', default: '180–365 days', priority: 'HIGH' },
    { icon: Mail, name: 'Emails', desc: 'Source email metadata', default: '180–365 days', priority: 'HIGH' },
    { icon: Bell, name: 'Notifications', desc: 'In-app alerts per user', default: '30 days', priority: 'MEDIUM' },
    { icon: Cpu, name: 'Processing Jobs', desc: 'Completed analysis jobs', default: '60 days', priority: 'MEDIUM' },
    { icon: BarChart3, name: 'Analysis Runs', desc: 'AI extraction JSON payloads', default: '60 days', priority: 'MEDIUM' },
    { icon: Webhook, name: 'Webhook Logs', desc: 'Delivery attempt history', default: '30 days', priority: 'LOW' },
    { icon: CreditCard, name: 'Metered Usage', desc: 'Billing records (reported only)', default: '24 months', priority: 'LOW' },
  ]

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <table className="w-full text-[11px]">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Data Type</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Description</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Suggested Default</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Priority</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {entities.map((e, i) => (
            <tr key={i}>
              <td className="px-3 py-2 flex items-center gap-1.5">
                <e.icon className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-gray-800 dark:text-gray-200 font-medium">{e.name}</span>
              </td>
              <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{e.desc}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{e.default}</td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                  e.priority === 'HIGH' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  e.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {e.priority}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Guide Export
// ─────────────────────────────────────────────────────────────────────────────
export default function DataRetentionGuide() {
  return (
    <div className="space-y-8">
      {/* Intro */}
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          The <strong>Data Retention</strong> page lets administrators configure automatic cleanup policies
          for different types of data in the system. This helps manage storage costs, maintain compliance
          with data regulations (GDPR, SOC2, etc.), and keep your workspace clean.
        </p>
        <InfoBox type="info">
          <strong>Admin only:</strong> Only users with the Admin role can access Data Retention settings.
          Navigate to <strong>Data Retention</strong> in the sidebar (under Billing &amp; Reporting).
        </InfoBox>
      </div>

      {/* Section 1 */}
      <Section number={1} title="Page Header & Tabs">
        <PageHeaderMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The page has two tabs:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-4 list-disc">
          <li><strong>Policies</strong> - Configure retention periods for each data type</li>
          <li><strong>Deletion Log</strong> - View audit trail of what was automatically deleted and when</li>
        </ul>
      </Section>

      {/* Section 2 */}
      <Section number={2} title="Understanding Data Types">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Eight types of data can accumulate over time. Each can have its own retention policy:
        </p>
        <EntityTypesTable />
        <InfoBox type="warning">
          <strong>Metered Usage:</strong> Billing records should be kept long enough for dispute resolution
          (typically 24 months). Only already-reported records are eligible for deletion.
        </InfoBox>
      </Section>

      {/* Section 3 */}
      <Section number={3} title="Configuring a Policy">
        <PolicyCardMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Each policy card has these controls:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 ml-4 list-disc">
          <li>
            <strong>Enable/Disable Toggle</strong> (top-right) - The policy only runs when enabled.
            You can configure everything first, then flip it on when ready.
          </li>
          <li>
            <strong>Retention Period (days)</strong> - Items older than this many days are eligible for deletion.
            Example: setting 180 means anything created more than 6 months ago will be cleaned up.
          </li>
          <li>
            <strong>Delete blob files</strong> (Documents only) - Choose whether to also delete the actual
            PDF file from Azure storage, or just the database record.
          </li>
          <li>
            <strong>Protected Statuses</strong> (Documents, Requests, Emails) - Items with these statuses
            are <em>never</em> deleted, regardless of age. Use this to protect in-progress work.
          </li>
        </ul>
      </Section>

      {/* Section 4 */}
      <Section number={4} title="Preview Before Enabling">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Always use the <strong>Preview</strong> button before enabling a policy.
            It shows exactly how many items would be affected without actually deleting anything.
          </p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The preview count updates whenever you change the retention period or status exclusions.
          This gives you confidence that the policy won't delete something unexpected.
        </p>
      </Section>

      {/* Section 5 */}
      <Section number={5} title="How Deletion Works">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-blue-600">1</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              A background process checks all enabled policies approximately <strong>once per hour</strong>.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-blue-600">2</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              For each policy, it finds items older than the retention period that don't have a protected status.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-blue-600">3</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Items are deleted in small batches (50 at a time) to avoid impacting system performance.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-blue-600">4</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Every deletion is logged in the <strong>Deletion Log</strong> for audit purposes.
            </p>
          </div>
        </div>
        <InfoBox type="success">
          <strong>Safety guarantee:</strong> Items that are actively being processed (pending, in progress,
          queued) are <em>never</em> deleted by retention policies, even if they exceed the age threshold.
        </InfoBox>
      </Section>

      {/* Section 6 */}
      <Section number={6} title="Deletion Log (Audit Trail)">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The <strong>Deletion Log</strong> tab shows a complete history of every item that was automatically
          removed by retention policies. Each entry includes:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-4 list-disc">
          <li>Data type (document, request, notification, etc.)</li>
          <li>Item name or identifier</li>
          <li>When it was deleted</li>
          <li>Which policy triggered the deletion</li>
        </ul>
        <InfoBox type="warning">
          <strong>Irreversible:</strong> Once data is deleted by a retention policy, it cannot be recovered.
          Make sure your retention periods align with your organisation's compliance requirements.
        </InfoBox>
      </Section>

      {/* Section 7 */}
      <Section number={7} title="Best Practices">
        <div className="space-y-2">
          {[
            { icon: CheckCircle2, text: 'Start with Preview to understand impact before enabling' },
            { icon: CheckCircle2, text: 'Protect "in_progress" and "pending" statuses for Documents/Requests/Emails' },
            { icon: CheckCircle2, text: 'Set notifications to 30 days - they pile up fast and have low archival value' },
            { icon: CheckCircle2, text: 'Keep metered usage for at least 12–24 months for billing disputes' },
            { icon: CheckCircle2, text: 'For analysis runs, even 60 days is generous - only the latest run per document matters' },
            { icon: CheckCircle2, text: 'Review the Deletion Log periodically to confirm policies are working as expected' },
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-2">
              <tip.icon className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-400">{tip.text}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}
